import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DataProtection, Header } from '@aptsi-types';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DataAssetMappingService, SecurityZoneSystemAssets } from '../../shared/services/data-asset-mapping.service';
// Adjust this import if KeycloakStateService is located in a different shared folder.
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { KeycloakService } from 'keycloak-angular';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from 'src/app/shared/dialogs/error-dialog/error-dialog.component';

type MappingRecord = DataProtection & Record<string, unknown>;
type WizardStep = 1 | 2 | 3 | 4;
type SelectionKind = 'securityZones' | 'systemAssets';
type SecurityZoneOption = 'select' | 'none';

interface ListState {
  rows: DataProtection[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  searchText: string;
  filterByHeader: string;
  isSearch: boolean;
  isLoading: boolean;
  isSearching: boolean;
  loadError: boolean;
  searchError: boolean;
  loaded: boolean;
}

interface PartialSecurityZoneReview {
  securityZoneId: string;
  securityZoneName: string;
  systemAssets: DataProtection[];
}

interface ResponseMetadata {
  totalDataAssets?: number | null;
  totalSystemAssets?: number | null;
  totalSecurityZones?: number | null;
  totalMappings?: number | null;
  totalPages?: number | null;
  currentPage?: number | null;
}

function createListState(): ListState {
  return {
    rows: [], currentPage: 1, totalPages: 0, totalItems: 0,
    searchText: '', filterByHeader: 'all', isSearch: false,
    isLoading: false, isSearching: false, loadError: false,
    searchError: false, loaded: false,
  };
}

@Component({
  selector: 'app-data-asset-mapping-create-or-update',
  templateUrl: './data-asset-mapping-create-or-update.component.html',
  styleUrls: ['./data-asset-mapping-create-or-update.component.scss'],
})
export class DataAssetMappingCreateOrUpdateComponent implements OnInit, OnDestroy {

  private securityZoneTableDataSource: DataProtection[] | null = null;
  private securityZoneTableDataCache: DataProtection[] = [];

  private systemAssetTableDataSource: DataProtection[] | null = null;
  private systemAssetTableDataCache: DataProtection[] = [];

  // Cache normalized rows for the partial-security-zone selection popup.
  private partialAssetTableDataSource: DataProtection[] | null = null;
  private partialAssetTableDataCache: DataProtection[] = [];

  readonly itemsPerPage = 10;
  readonly steps: Array<{ id: WizardStep; label: string; icon: string }> = [
    { id: 1, label: 'Mapping Details', icon: 'bi-card-text' },
    { id: 2, label: 'Security Zones', icon: 'bi-shield-check' },
    { id: 3, label: 'System Assets', icon: 'bi-hdd-network' },
    { id: 4, label: 'Review', icon: 'bi-clipboard-check' },
  ];

  readonly securityZoneHeaders: Header[] = [
    { name: 'Security Zone ID', property: 'securityZoneId', showInDropdown: true, description: 'Unique identifier of the security zone' },
    { name: 'Security Zone Name', property: 'securityZoneName', showInDropdown: true, description: 'Name of the security zone' },
    { name: 'Description', property: 'securityZoneDescription', showInDropdown: true, description: 'Description of the security zone' },
    { name: 'Zone Type', property: 'securityZoneType', showInDropdown: true, description: 'Type of the security zone' },
  ];

  readonly systemAssetHeaders: Header[] = [
    { name: 'System Asset ID', property: 'systemAssetId', showInDropdown: true, description: 'Unique identifier of the system asset' },
    { name: 'System Asset Name', property: 'systemAssetName', showInDropdown: true, description: 'Name of the system asset' },
    { name: 'Description', property: 'systemAssetDescription', showInDropdown: true, description: 'Description of the system asset' },
  ];

  readonly noActionsConfig = { map: false, update: false, view: false, delete: false, import: false, configure: false, submit: false, showAssets: false };
  readonly selectionRowKey: Record<SelectionKind, string> = { securityZones: 'securityZoneId', systemAssets: 'systemAssetId' };

  form: FormGroup;
  currentStep: WizardStep = 1;
  isCreate = true;
  dataAssetId = '';
  mappingId = '';
  isSubmitting = false;
  submitError = '';
  submitSuccess = false;
  pageError = '';

  readonly securityZonesState = createListState();
  readonly systemAssetsState = createListState();
  selectedSecurityZoneIds: string[] = [];
  selectedSystemAssetIds: string[] = [];
  securityZoneOption: SecurityZoneOption = 'select';

  // A zone in this map is represented as a partial selection rather than a full zone.
  readonly partialZoneSystemAssetIds = new Map<string, string[]>();
  private readonly partialZoneSystemAssetRecords = new Map<string, Map<string, DataProtection>>();
  partialAssetsState = createListState();
  isPartialAssetDialogOpen = false;
  currentPartialSecurityZoneId = '';
  currentPartialSecurityZoneName = '';
  partialAssetSelectedIds: string[] = [];
  private readonly partialAssetSelectedRecords = new Map<string, DataProtection>();
  partialAssetDialogError = '';
  private readonly partialAssetSearch$ = new Subject<void>();
  private mappedSystemAssetRows: DataProtection[] = [];
  private mappedSecurityZoneRows: DataProtection[] = [];
  private readonly selectedSecurityZoneRecords = new Map<string, DataProtection>();
  private readonly selectedSystemAssetRecords = new Map<string, DataProtection>();
  private readonly destroy$ = new Subject<void>();
  /** Request counters prevent stale page/search responses from replacing newer results. */
  private readonly listRequestSequences = new WeakMap<ListState, number>();
  private readonly securityZoneSearch$ = new Subject<void>();
  private readonly systemAssetSearchQuery$ = new Subject<void>();

    readonly securityZoneActionsConfig = {

    view: false,

  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly mappingService: DataAssetMappingService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly cdr: ChangeDetectorRef,
    private readonly  dialog: MatDialog
  ) {
    this.form = this.fb.group({
      mappingName: ['', [Validators.required, Validators.maxLength(120)]],
      mappingDescription: ['', [Validators.required, Validators.maxLength(1000)]],
    });
  }

  ngOnInit(): void {
    this.dataAssetId = this.route.snapshot.paramMap.get('dataAssetId') ?? '';
    this.isCreate = this.route.snapshot.data['create'] === true;

    if (!this.dataAssetId) {
      this.pageError = 'The data asset ID is missing from the route.';
      return;
    }

    this.securityZoneSearch$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => void this.fetchSecurityZones(true));
    this.systemAssetSearchQuery$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => void this.fetchSystemAssets(true));
    this.partialAssetSearch$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => void this.fetchPartialSecurityZoneAssets(true));

    if (this.isCreate) {
      void this.fetchSecurityZones(false);
    } else {
      void this.loadExistingMapping();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeListState(): ListState {
    return this.currentStep === 2 ? this.securityZonesState : this.systemAssetsState;
  }

  get activeHeaders(): Header[] {
    return this.currentStep === 2 ? this.securityZoneHeaders : this.systemAssetHeaders;
  }

  get displayedRows(): DataProtection[] {
    return this.activeListState.rows;
  }

get securityZoneTableData(): DataProtection[] {
  const source = this.securityZonesState.rows;

  // Normalize only when the underlying rows change.
  if (source !== this.securityZoneTableDataSource) {
    this.securityZoneTableDataSource = source;

    this.securityZoneTableDataCache = this.normalizeTableRows(
      source,
      this.securityZoneHeaders
    );
  }

  return this.securityZoneTableDataCache;
}

get systemAssetTableData(): DataProtection[] {
  const source = this.systemAssetsState.rows;

  // Normalize only when the underlying rows change.
  if (source !== this.systemAssetTableDataSource) {
    this.systemAssetTableDataSource = source;

    this.systemAssetTableDataCache = this.normalizeTableRows(
      source,
      this.systemAssetHeaders
    );
  }

  return this.systemAssetTableDataCache;
}

  get selectedSecurityZones(): DataProtection[] {
    const byId = new Map<string, DataProtection>(this.selectedSecurityZoneRecords);
    [...this.mappedSecurityZoneRows, ...this.securityZonesState.rows].forEach(row => {
      const id = this.asId(row.securityZoneId);
      if (id && this.selectedSecurityZoneIds.includes(id)) byId.set(id, row);
    });
    return this.selectedSecurityZoneIds.map(id => byId.get(id) ?? ({ securityZoneId: id, securityZoneName: 'Selected security zone' } as DataProtection));
  }

  get selectedSystemAssets(): DataProtection[] {
    const byId = new Map<string, DataProtection>(this.selectedSystemAssetRecords);
    [...this.mappedSystemAssetRows, ...this.systemAssetsState.rows].forEach(row => {
      const id = this.asId(row.systemAssetId);
      if (id && this.selectedSystemAssetIds.includes(id)) byId.set(id, row);
    });
    return this.selectedSystemAssetIds.map(id => byId.get(id) ?? ({ systemAssetId: id, systemAssetName: 'Selected system asset' } as DataProtection));
  }

  get selectedFullSecurityZones(): DataProtection[] {
    return this.selectedSecurityZones.filter(zone => {
      const id = this.asId(zone.securityZoneId);
      return !!id && !this.partialZoneSystemAssetIds.has(id);
    });
  }

  get fullSecurityZoneIds(): string[] {
    return this.selectedSecurityZoneIds.filter(id => !this.partialZoneSystemAssetIds.has(id));
  }

  get selectedPartialSecurityZones(): PartialSecurityZoneReview[] {
    const zoneById = new Map<string, DataProtection>();
    this.selectedSecurityZones.forEach(zone => {
      const zoneId = this.asId(zone.securityZoneId);
      if (zoneId) zoneById.set(zoneId, zone);
    });
    return [...this.partialZoneSystemAssetIds.entries()].map(([securityZoneId, assetIds]) => {
      const firstAssetForZone = this.partialZoneSystemAssetRecords.get(securityZoneId)?.values().next().value as DataProtection | undefined;
      const zone = zoneById.get(securityZoneId) ?? firstAssetForZone;
      const records = this.partialZoneSystemAssetRecords.get(securityZoneId) ?? new Map<string, DataProtection>();
      const systemAssets = assetIds.map(id => records.get(id) ?? ({ systemAssetId: id, systemAssetName: 'Selected system asset' } as DataProtection));
      return {
        securityZoneId,
        securityZoneName: zone?.securityZoneName || securityZoneId,
        systemAssets,
      };
    });
  }

  get securityZoneSystemAssetsPayload(): SecurityZoneSystemAssets[] {
    // Avoid Array.prototype.flatMap so this remains compatible with projects
    // whose TypeScript lib target predates ES2019.
    const payload: SecurityZoneSystemAssets[] = [];
    this.partialZoneSystemAssetIds.forEach((systemAssetIds, securityZoneId) => {
      systemAssetIds.forEach(systemAssetId => {
        payload.push({ securityZoneId, systemAssetId });
      });
    });
    return payload;
  }

  get hasAnySelectedMappingTarget(): boolean {
    return this.fullSecurityZoneIds.length > 0 || this.securityZoneSystemAssetsPayload.length > 0 || this.selectedSystemAssetIds.length > 0;
  }

  get partialAssetTableData(): DataProtection[] {
    const source = this.partialAssetsState.rows;
    if (source !== this.partialAssetTableDataSource) {
      this.partialAssetTableDataSource = source;
      this.partialAssetTableDataCache = this.normalizeTableRows(source, this.systemAssetHeaders);
    }
    return this.partialAssetTableDataCache;
  }

  get isLastStep(): boolean { return this.currentStep === 4; }

  async loadExistingMapping(): Promise<void> {
    this.pageError = '';
    this.securityZonesState.isLoading = true;
    this.systemAssetsState.isLoading = true;
    try {
      const mappingResponse = await this.mappingService.FetchMappingDetailsByDataAssetId(this.dataAssetId).toPromise();
      await this.delay(300);
      const zonesResponse = await this.mappingService.FetchMappedSecurityZonesByDataAssetId(0, this.dataAssetId).toPromise();
      await this.delay(300);
      const assetsResponse = await this.mappingService.FetchMappedSystemAssetsByDataAssetId(0, this.dataAssetId).toPromise();
      await this.delay(300);
      const partialZonesResponse = await this.mappingService.FetchMappedPartialSecurityZonesByDataAssetId(this.dataAssetId).toPromise();

      const mapping = this.asRows(mappingResponse)[0];
      if (mapping) {
        this.mappingId = this.asId(mapping.mappingId);
        this.form.patchValue({
          mappingName: mapping.mappingName ?? '',
          mappingDescription: mapping.mappingDescription ?? '',
        });
      }
      this.form.get('mappingName')?.disable({ emitEvent: false });

      const zoneResult = this.applyResponse(this.securityZonesState, zonesResponse, 'totalSecurityZones');
      this.mappedSecurityZoneRows = zoneResult.rows;
      this.selectedSecurityZoneRecords.clear();
      zoneResult.rows.forEach(row => {
        const id = this.asId(row.securityZoneId);
        if (id) this.selectedSecurityZoneRecords.set(id, row);
      });
      this.securityZonesState.loaded = true;

      const partialRows = this.asRows(partialZonesResponse);
      this.partialZoneSystemAssetIds.clear();
      this.partialZoneSystemAssetRecords.clear();
      const partialIdsByZone = new Map<string, string[]>();
      const partialRecordsByZone = new Map<string, Map<string, DataProtection>>();
      partialRows.forEach(row => {
        const zoneId = this.asId(row.securityZoneId);
        const assetId = this.asId(row.systemAssetId);
        if (!zoneId || !assetId) return;
        const ids = partialIdsByZone.get(zoneId) ?? [];
        if (!ids.includes(assetId)) ids.push(assetId);
        partialIdsByZone.set(zoneId, ids);
        const records = partialRecordsByZone.get(zoneId) ?? new Map<string, DataProtection>();
        records.set(assetId, row);
        partialRecordsByZone.set(zoneId, records);
        if (!this.selectedSecurityZoneRecords.has(zoneId)) this.selectedSecurityZoneRecords.set(zoneId, row);
      });
      partialIdsByZone.forEach((ids, zoneId) => this.partialZoneSystemAssetIds.set(zoneId, ids));
      partialRecordsByZone.forEach((records, zoneId) => this.partialZoneSystemAssetRecords.set(zoneId, records));

      // Mapped zones are complete-zone associations; partial-zone rows are represented separately.
      this.selectedSecurityZoneIds = this.uniqueIds([
        ...zoneResult.rows.map(row => row.securityZoneId),
        ...partialRows.map(row => row.securityZoneId),
      ]);
      this.securityZoneOption = this.selectedSecurityZoneIds.length ? 'select' : 'none';

      const assetResult = this.applyResponse(this.systemAssetsState, assetsResponse, 'totalSystemAssets');
      this.mappedSystemAssetRows = assetResult.rows;
      this.selectedSystemAssetIds = this.uniqueIds(assetResult.rows.map(row => row.systemAssetId));
      this.selectedSystemAssetRecords.clear();
      assetResult.rows.forEach(row => {
        const id = this.asId(row.systemAssetId);
        if (id) this.selectedSystemAssetRecords.set(id, row);
      });
      this.systemAssetsState.loaded = true;

      // Populate the selector with available zones while retaining the mapped rows and IDs above.
      this.securityZonesState.currentPage = 1;
      await this.fetchSecurityZones(false);
      this.systemAssetsState.currentPage = 1;
      await this.fetchSystemAssets(false);
    } catch (error) {
      console.error('Unable to load mapping details for update:', error);
      this.pageError = 'Unable to load the existing mapping. Please retry.';
    } finally {
      this.securityZonesState.isLoading = false;
      this.systemAssetsState.isLoading = false;
      this.refreshView();
    }
  }

  private delay(milliseconds: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

  async fetchSecurityZones(search = false): Promise<void> {
    const state = this.securityZonesState;
    if (search && !state.searchText.trim()) {
      state.isSearch = false;
      state.currentPage = 1;
      await this.fetchSecurityZones(false);
      return;
    }

    const requestId = this.beginListRequest(state);
    // These mapping endpoints expect a one-based currentPage (1 = first page).
    // Pass state.currentPage directly; subtracting one makes page 2 request page 1.
    state.isSearch = search;
    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
      state.searchError = false;
    }

    try {
      const response = search
        ? await this.mappingService.FetchSecurityZonesSearch(
            state.currentPage - 1,
            state.searchText.trim(),
            state.filterByHeader,
          ).toPromise()
        : await this.mappingService.FetchSecurityZones(state.currentPage - 1).toPromise();

      if (!this.isLatestListRequest(state, requestId)) return;

      this.applyResponse(state, response, 'totalSecurityZones');
      state.loaded = true;

      if (this.adjustPageToBackendTotal(state)) {
        await this.fetchSecurityZones(search);
        return;
      }
    } catch (error) {
      if (!this.isLatestListRequest(state, requestId)) return;
      console.error('Unable to fetch security zones:', error);
      if (search) state.searchError = true;
      else state.loadError = true;
      state.rows = [];
      state.totalItems = 0;
      state.totalPages = 0;
      state.loaded = false;
    } finally {
      if (this.isLatestListRequest(state, requestId)) {
        state.isLoading = false;
        state.isSearching = false;
        this.refreshView();
      }
    }
  }

  async fetchSystemAssets(search = false): Promise<void> {
    const state = this.systemAssetsState;
    if (search && !state.searchText.trim()) {
      state.isSearch = false;
      state.currentPage = 1;
      await this.fetchSystemAssets(false);
      return;
    }

    const requestId = this.beginListRequest(state);
    state.isSearch = search;
    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
      state.searchError = false;
    }

    try {
      // Step 3 intentionally lists assets that are not selected as part of a security zone.
      // The API receives the selected zone IDs so it can exclude/consider those associations.
      const zoneIds = [...this.selectedSecurityZoneIds];
      const response = search
        ? await this.mappingService.FetchSystemAssetsForMappingSearch(
            state.currentPage - 1,
            state.searchText.trim(),
            state.filterByHeader,
            zoneIds,
          ).toPromise()
        : await this.mappingService.FetchSystemAssetsForMapping(state.currentPage - 1, zoneIds).toPromise();

      if (!this.isLatestListRequest(state, requestId)) return;

      const result = this.applyResponse(state, response, 'totalSystemAssets');
      const returnedIds = new Set(result.rows.map(row => this.asId(row.systemAssetId)));
      const retainedMapped = this.mappedSystemAssetRows.filter(row => {
        const id = this.asId(row.systemAssetId);
        return id && this.selectedSystemAssetIds.includes(id) && !returnedIds.has(id);
      });
      state.rows = [...retainedMapped, ...result.rows];
      state.loaded = true;

      if (this.adjustPageToBackendTotal(state)) {
        await this.fetchSystemAssets(search);
        return;
      }
    } catch (error) {
      if (!this.isLatestListRequest(state, requestId)) return;
      console.error('Unable to fetch unzoned system assets for mapping:', error);
      if (search) state.searchError = true;
      else state.loadError = true;
      state.rows = [];
      state.totalItems = 0;
      state.totalPages = 0;
      state.loaded = false;
    } finally {
      if (this.isLatestListRequest(state, requestId)) {
        state.isLoading = false;
        state.isSearching = false;
        this.refreshView();
      }
    }
  }

  setSecurityZoneOption(option: SecurityZoneOption): void {
    this.securityZoneOption = option;
    this.submitError = '';
    if (option === 'none') {
      this.selectedSecurityZoneIds = [];
      this.selectedSecurityZoneRecords.clear();
      this.partialZoneSystemAssetIds.clear();
      this.partialZoneSystemAssetRecords.clear();
      this.isPartialAssetDialogOpen = false;
    }
    this.systemAssetsState.loaded = false;
    this.refreshView();
  }

  openPartialSecurityZoneAssets(zone: DataProtection): void {
    const zoneId = this.asId(zone.securityZoneId);
    if (!zoneId) return;

    this.currentPartialSecurityZoneId = zoneId;
    this.currentPartialSecurityZoneName = zone.securityZoneName || zoneId;
    this.partialAssetSelectedIds = [...(this.partialZoneSystemAssetIds.get(zoneId) ?? [])];
    this.partialAssetSelectedRecords.clear();
    const savedRecords = this.partialZoneSystemAssetRecords.get(zoneId);
    savedRecords?.forEach((row, id) => this.partialAssetSelectedRecords.set(id, row));
    this.partialAssetDialogError = '';
    this.partialAssetsState = createListState();
    this.isPartialAssetDialogOpen = true;
    void this.fetchPartialSecurityZoneAssets(false);
  }

  async fetchPartialSecurityZoneAssets(search = false): Promise<void> {
    const state = this.partialAssetsState;
    if (!this.currentPartialSecurityZoneId) return;
    if (search && !state.searchText.trim()) {
      state.isSearch = false;
      state.currentPage = 1;
      await this.fetchPartialSecurityZoneAssets(false);
      return;
    }

    const requestId = this.beginListRequest(state);
    state.isSearch = search;
    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
      state.searchError = false;
    }

    try {
      const response = search
        ? await this.mappingService.FetchSystemAssetsBySecurityZoneIdSearch(
            state.currentPage - 1,
            state.searchText.trim(),
            state.filterByHeader,
            this.currentPartialSecurityZoneId,
          ).toPromise()
        : await this.mappingService.FetchSystemAssetsBySecurityZoneId(
            state.currentPage - 1,
            this.currentPartialSecurityZoneId,
          ).toPromise();

      if (!this.isLatestListRequest(state, requestId)) return;

      this.applyResponse(state, response, 'totalSystemAssets');
      state.loaded = true;

      if (this.adjustPageToBackendTotal(state)) {
        await this.fetchPartialSecurityZoneAssets(search);
        return;
      }
    } catch (error) {
      if (!this.isLatestListRequest(state, requestId)) return;
      console.error('Unable to fetch system assets for security zone:', error);
      if (search) state.searchError = true;
      else state.loadError = true;
      state.rows = [];
      state.totalItems = 0;
      state.totalPages = 0;
      state.loaded = false;
    } finally {
      if (this.isLatestListRequest(state, requestId)) {
        state.isLoading = false;
        state.isSearching = false;
        this.refreshView();
      }
    }
  }

  onPartialAssetSearchInput(): void {
    this.partialAssetsState.currentPage = 1;
    this.partialAssetSearch$.next();
  }

  onPartialAssetFilterChanged(): void {
    this.partialAssetsState.currentPage = 1;
    void this.fetchPartialSecurityZoneAssets(!!this.partialAssetsState.searchText.trim());
  }

  clearPartialAssetSearch(): void {
    this.partialAssetsState.searchText = '';
    this.partialAssetsState.filterByHeader = 'all';
    this.partialAssetsState.currentPage = 1;
    void this.fetchPartialSecurityZoneAssets(false);
  }

  submitPartialAssetSearch(): void {
    void this.fetchPartialSecurityZoneAssets(!!this.partialAssetsState.searchText.trim());
  }

  changePartialAssetPage(page: number | { pageIndex?: number; currentPage?: number }): void {
    const state = this.partialAssetsState;
    const requested = typeof page === 'number' ? page : (page.currentPage ?? ((page.pageIndex ?? 0) + 1));
    if (!Number.isInteger(requested) || requested < 1 || requested > state.totalPages || requested === state.currentPage) {
      return;
    }
    state.currentPage = requested;
    void this.fetchPartialSecurityZoneAssets(state.isSearch);
  }

  onPartialAssetRowsChange(selectedRows: DataProtection[]): void {
    const visibleIds = this.uniqueIds(this.partialAssetsState.rows.map(row => row.systemAssetId));
    const selectedVisibleIds = this.uniqueIds((selectedRows ?? []).map(row => row.systemAssetId));
    const nextIds = this.partialAssetSelectedIds.filter(id => !visibleIds.includes(id));
    nextIds.push(...selectedVisibleIds.filter(id => !nextIds.includes(id)));
    visibleIds.forEach(id => {
      if (!selectedVisibleIds.includes(id)) this.partialAssetSelectedRecords.delete(id);
    });
    (selectedRows ?? []).forEach(row => {
      const id = this.asId(row.systemAssetId);
      if (id) this.partialAssetSelectedRecords.set(id, row);
    });
    this.partialAssetSelectedIds = nextIds;
    this.partialAssetDialogError = '';
    this.refreshView();
  }

  savePartialSecurityZoneAssets(): void {
    if (!this.currentPartialSecurityZoneId || !this.partialAssetSelectedIds.length) {
      this.partialAssetDialogError = 'Select at least one system asset, or close this window and use the complete security zone.';
      return;
    }
    this.partialZoneSystemAssetIds.set(this.currentPartialSecurityZoneId, [...this.partialAssetSelectedIds]);
    this.partialZoneSystemAssetRecords.set(this.currentPartialSecurityZoneId, new Map(this.partialAssetSelectedRecords));
    this.isPartialAssetDialogOpen = false;
    this.partialAssetDialogError = '';
    this.refreshView();
  }

  useCompleteSecurityZone(securityZoneId: string): void {
    this.partialZoneSystemAssetIds.delete(securityZoneId);
    this.partialZoneSystemAssetRecords.delete(securityZoneId);
    this.refreshView();
  }

  closePartialSecurityZoneAssets(): void {
    this.isPartialAssetDialogOpen = false;
    this.partialAssetDialogError = '';
  }

  onSearchInput(kind: SelectionKind): void {
    const state = kind === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
    state.currentPage = 1;
    if (kind === 'securityZones') this.securityZoneSearch$.next();
    else this.systemAssetSearchQuery$.next();
  }

  onFilterChanged(kind: SelectionKind): void {
    const state = kind === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
    state.currentPage = 1;
    if (state.searchText.trim()) {
      if (kind === 'securityZones') void this.fetchSecurityZones(true);
      else void this.fetchSystemAssets(true);
    } else {
      if (kind === 'securityZones') void this.fetchSecurityZones(false);
      else void this.fetchSystemAssets(false);
    }
  }

  submitSearch(kind: SelectionKind): void {
    if (kind === 'securityZones') void this.fetchSecurityZones(!!this.securityZonesState.searchText.trim());
    else void this.fetchSystemAssets(!!this.systemAssetsState.searchText.trim());
  }

  clearSearch(kind: SelectionKind): void {
    const state = kind === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
    state.searchText = '';
    state.filterByHeader = 'all';
    state.isSearch = false;
    state.currentPage = 1;
    if (kind === 'securityZones') void this.fetchSecurityZones(false);
    else void this.fetchSystemAssets(false);
  }

  changePage(kind: SelectionKind, page: number | { pageIndex?: number; currentPage?: number }): void {
    const state = kind === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
    const requested = typeof page === 'number' ? page : (page.currentPage ?? ((page.pageIndex ?? 0) + 1));
    if (!Number.isInteger(requested) || requested < 1 || requested > state.totalPages || requested === state.currentPage) {
      return;
    }
    state.currentPage = requested;
    if (kind === 'securityZones') void this.fetchSecurityZones(state.isSearch);
    else void this.fetchSystemAssets(state.isSearch);
  }

  onSelectedRowsChange(kind: SelectionKind, selectedRows: DataProtection[]): void {
    const state = kind === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
    const key = this.selectionRowKey[kind];
    const visibleIds = this.uniqueIds(state.rows.map(row => (row as MappingRecord)[key] as string | null | undefined));
    const selectedVisibleIds = this.uniqueIds((selectedRows ?? []).map(row => (row as MappingRecord)[key] as string | null | undefined));
    const currentIds = kind === 'securityZones' ? this.selectedSecurityZoneIds : this.selectedSystemAssetIds;
    // Replace selection only for rows currently loaded; selections on other pages remain intact.
    const nextIds = currentIds.filter(id => !visibleIds.includes(id));
    nextIds.push(...selectedVisibleIds.filter(id => !nextIds.includes(id)));
    const recordCache = kind === 'securityZones' ? this.selectedSecurityZoneRecords : this.selectedSystemAssetRecords;
    visibleIds.forEach(id => {
      if (!selectedVisibleIds.includes(id)) recordCache.delete(id);
    });
    (selectedRows ?? []).forEach(row => {
      const id = this.asId((row as MappingRecord)[key]);
      if (id) recordCache.set(id, row);
    });
    if (kind === 'securityZones') {
      const previous = this.selectedSecurityZoneIds;
      this.selectedSecurityZoneIds = nextIds;
      previous.filter(id => !nextIds.includes(id)).forEach(id => {
        this.partialZoneSystemAssetIds.delete(id);
        this.partialZoneSystemAssetRecords.delete(id);
        this.selectedSecurityZoneRecords.delete(id);
      });
      if (nextIds.length) this.securityZoneOption = 'select';
      if (previous.join('|') !== nextIds.join('|')) this.systemAssetsState.loaded = false;
    } else {
      this.selectedSystemAssetIds = nextIds;
    }
    this.refreshView();
  }

  async nextStep(): Promise<void> {
    this.submitError = '';
    if (this.currentStep === 1) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
      this.currentStep = 2;
      if (!this.securityZonesState.loaded) await this.fetchSecurityZones(false);
      return;
    }
    if (this.currentStep === 2) {
      if (this.securityZoneOption === 'select' && !this.selectedSecurityZoneIds.length) {
        this.submitError = 'Select at least one security zone, or choose “No security zone association” to skip zone selection.';
        return;
      }
      this.currentStep = 3;
      this.systemAssetsState.currentPage = 1;
      this.systemAssetsState.searchText = '';
      this.systemAssetsState.filterByHeader = 'all';
      this.systemAssetsState.isSearch = false;
      await this.fetchSystemAssets(false);
      return;
    }
    if (this.currentStep === 3) {
      if (!this.hasAnySelectedMappingTarget) {
        this.submitError = 'Select at least one unzoned system asset, a complete security zone, or partial security-zone assets before continuing.';
        return;
      }
      this.currentStep = 4;
    }
  }

  previousStep(): void {
    this.submitError = '';
    if (this.currentStep > 1) this.currentStep = (this.currentStep - 1) as WizardStep;
  }

  goToStep(step: WizardStep): void {
    if (step < this.currentStep) {
      this.currentStep = step;
      this.submitError = '';
    }
  }

  async finish(): Promise<void> {
    this.submitError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.hasAnySelectedMappingTarget) {
      this.submitError = 'Complete the mapping details and select at least one mapping target: a complete zone, partial-zone assets, or unzoned system assets.';
      return;
    }

    this.isSubmitting = true;
    try {
      const values = this.form.getRawValue();
      const securityZoneIds = [...this.fullSecurityZoneIds];
      const securityZoneSystemAssets = this.securityZoneSystemAssetsPayload;
      if (this.isCreate) {
        const mappingCreatedBy = await this.getCurrentUsername();
        const mappingCreatedByKeycloakId = this.keycloakStateService.getUserId() ?? '';
        await this.mappingService.createMapping(
          this.dataAssetId,
          String(values.mappingName).trim(),
          String(values.mappingDescription).trim(),
          mappingCreatedBy,
          mappingCreatedByKeycloakId,
          this.selectedSystemAssetIds,
          securityZoneIds,
          securityZoneSystemAssets,
        ).toPromise();
        this.openSuccessDialog(this.isCreate);
      } else {
        await this.mappingService.UpdateMapping(
          this.mappingId,
          String(values.mappingDescription).trim(),
          this.selectedSystemAssetIds,
          securityZoneIds,
          securityZoneSystemAssets,
        ).toPromise();
        this.openSuccessDialog(this.isCreate);
      }
      this.submitSuccess = true;
    } catch (error) {
      console.error('Unable to save data asset mapping:', error);
      this.openErrorDialog(this.isCreate ? 'Unable to create mapping. Please try again.' : 'Unable to update mapping. Please try again.');
    } finally {
      this.isSubmitting = false;
      this.refreshView();
    }
  }

    openSuccessDialog(isCreate: boolean): void {
      const dialogRef = this.dialog.open(SuccessDialogComponent, {
        width: "250px",
        data: {
          isCreate: isCreate,
          isCreateUpdate: true,
          title: "Data Asset Mapping",
        },
      });
     
        dialogRef.afterClosed().subscribe((result) => {
          this.router.navigate([
            "data-protection/auth-tools/data-asset-mapping",
          ]);
        });
      
    }

      openErrorDialog(errorMessage: string): void {
        const dialogRef = this.dialog.open(ErrorDialogComponent, {
          width: "250px",
          data: { errorMessage: errorMessage },
        });
    
        dialogRef.afterClosed().subscribe((result) => {});
      }

  cancel(): void {
    void this.router.navigate(['/data-protection/auth-tools/data-asset-mapping']);
  }

  private async getCurrentUsername(): Promise<string> {
    try {
      const username = this.keycloakService.getUsername();
      if (username) return username;
      const profile = await this.keycloakService.loadUserProfile();
      return profile.username ?? profile.firstName ?? '';
    } catch {
      return '';
    }
  }

  private applyResponse(
    state: ListState,
    response: unknown,
    totalKey: keyof ResponseMetadata,
  ): { rows: DataProtection[]; metadata?: ResponseMetadata } {
    const normalized = this.normalizeListResponse(response);
    const totalFromBackend = normalized.metadata?.[totalKey];

    state.rows = normalized.rows;
    state.totalItems = this.toNonNegativeNumber(totalFromBackend, normalized.rows.length);
    // The backend is the source of truth. Do not estimate totalPages from row counts.
    state.totalPages = this.toNonNegativeNumber(normalized.metadata?.totalPages, 0);

    return normalized;
  }

  private normalizeListResponse(response: unknown): {
    rows: DataProtection[];
    metadata?: ResponseMetadata;
  } {
    if (Array.isArray(response)) {
      const rows = response.filter(
        (item): item is DataProtection => !!item && typeof item === 'object',
      );
      return { rows, metadata: this.findMetadata(response) };
    }

    if (response && typeof response === 'object') {
      const wrapper = response as Record<string, unknown>;
      const rowKeys = [
        'securityZones',
        'systemAssets',
        'dataAssets',
        'data',
        'content',
        'items',
        'results',
        'records',
        'assets',
        'mappedSecurityZones',
        'mappedSystemAssets',
      ];

      for (const key of rowKeys) {
        const value = wrapper[key];
        if (!Array.isArray(value)) continue;

        const rawRows = value as unknown[];
        const rows = rawRows.filter(
          (item): item is DataProtection => !!item && typeof item === 'object',
        );
        const rowMetadata = this.findMetadata(rawRows);
        const wrapperMetadata = this.readMetadata(wrapper);
        const nestedMetadata = this.readMetadata(wrapper['metadata'] ?? wrapper['meta']);
        const metadata = {
          ...(rowMetadata ?? {}),
          ...(wrapperMetadata ?? {}),
          ...(nestedMetadata ?? {}),
        };

        return {
          rows,
          metadata: Object.keys(metadata).length ? metadata : undefined,
        };
      }

      return { rows: [], metadata: this.readMetadata(wrapper) };
    }

    return { rows: [] };
  }

  private asRows(response: unknown): DataProtection[] {
    return this.normalizeListResponse(response).rows;
  }

  private findMetadata(rows: unknown[]): ResponseMetadata | undefined {
    for (const candidate of rows) {
      const metadata = this.readMetadata(candidate);
      if (metadata) return metadata;
    }
    return undefined;
  }

  private readMetadata(value: unknown): ResponseMetadata | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;

    const record = value as Record<string, unknown>;
    const metadata: ResponseMetadata = {};
    const keys: Array<keyof ResponseMetadata> = [
      'totalDataAssets',
      'totalSystemAssets',
      'totalSecurityZones',
      'totalMappings',
      'totalPages',
      'currentPage',
    ];

    for (const key of keys) {
      if (record[key] === undefined || record[key] === null || record[key] === '') continue;
      metadata[key] = this.toNonNegativeNumber(record[key], 0);
    }

    return Object.keys(metadata).length ? metadata : undefined;
  }

  private beginListRequest(state: ListState): number {
    const requestId = (this.listRequestSequences.get(state) ?? 0) + 1;
    this.listRequestSequences.set(state, requestId);
    return requestId;
  }

  private isLatestListRequest(state: ListState, requestId: number): boolean {
    return this.listRequestSequences.get(state) === requestId;
  }

  /** Clamp to the backend's page count and signal when the current page must be refetched. */
  private adjustPageToBackendTotal(state: ListState): boolean {
    if (state.totalPages <= 0) {
      state.currentPage = 1;
      return false;
    }

    if (state.currentPage > state.totalPages) {
      state.currentPage = state.totalPages;
      return true;
    }

    if (state.currentPage < 1) state.currentPage = 1;
    return false;
  }

  private normalizeTableRows(rows: DataProtection[], headers: Header[]): DataProtection[] {
    return rows.map(item => {
      const normalized: Record<string, unknown> = { ...(item as MappingRecord) };
      headers.forEach(header => {
        const value = normalized[header.property];
        normalized[header.property] = value === null || value === undefined || String(value).trim() === '' ? '—' : value;
      });
      return normalized as unknown as DataProtection;
    });
  }

  private uniqueIds(values: Array<string | null | undefined>): string[] {
    return [...new Set(values.map(value => this.asId(value)).filter(Boolean))];
  }

  private asId(value: unknown): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private toNonNegativeNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private refreshView(): void {
    this.cdr.markForCheck();
  }
}
